import { DollarSign, TrendingUp, FileText, AlertTriangle, Users, Wallet } from 'lucide-react';
import { formatEuro } from '../../utils/currency';
import type { FinancialSummary } from '../../types/financialReport';

interface SummaryCardsProps {
  summary: FinancialSummary;
  loading: boolean;
}

function MetricCard({
  label,
  value,
  icon: Icon,
  color,
  subtitle,
  loading,
}: {
  label: string;
  value: number;
  icon: typeof DollarSign;
  color: string;
  subtitle?: string;
  loading: boolean;
}) {
  const colorMap: Record<string, { bg: string; icon: string; ring: string }> = {
    green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-100' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', ring: 'ring-blue-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', ring: 'ring-amber-100' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', ring: 'ring-red-100' },
    slate: { bg: 'bg-slate-50', icon: 'text-slate-600', ring: 'ring-slate-100' },
    teal: { bg: 'bg-teal-50', icon: 'text-teal-600', ring: 'ring-teal-100' },
  };

  const c = colorMap[color] || colorMap.slate;

  return (
    <div className={`bg-white rounded-card border border-gray-100 shadow-card p-5 ring-1 ${c.ring} transition-all hover:shadow-card-hover`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`${c.bg} rounded-lg p-2.5`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{label}</p>
      {loading ? (
        <div className="h-7 w-32 bg-gray-200 rounded animate-pulse" />
      ) : (
        <p className="text-xl font-bold text-gray-900">{formatEuro(value)}</p>
      )}
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
}

export function SummaryCards({ summary, loading }: SummaryCardsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <MetricCard
        label="Total Fees"
        value={summary.total_fees}
        icon={DollarSign}
        color="green"
        subtitle={`${summary.campaigns_completed + summary.campaigns_billing + summary.campaigns_in_progress + summary.campaigns_consultation} campaigns`}
        loading={loading}
      />
      <MetricCard
        label="Commissions"
        value={summary.total_commissions}
        icon={Wallet}
        color="teal"
        subtitle="Agency revenue"
        loading={loading}
      />
      <MetricCard
        label="Client Invoiced"
        value={summary.total_client_invoiced}
        icon={FileText}
        color="blue"
        subtitle="Receivables issued"
        loading={loading}
      />
      <MetricCard
        label="Influencer Invoiced"
        value={summary.total_influencer_invoiced}
        icon={Users}
        color="amber"
        subtitle="Payables issued"
        loading={loading}
      />
      <MetricCard
        label="Pipeline"
        value={summary.pipeline_fees}
        icon={TrendingUp}
        color="slate"
        subtitle={`${summary.campaigns_consultation} in consultation`}
        loading={loading}
      />
      <MetricCard
        label="Outstanding Receivables"
        value={summary.client_balance_outstanding}
        icon={AlertTriangle}
        color="red"
        subtitle={summary.client_overdue > 0 ? `${formatEuro(summary.client_overdue)} overdue` : 'No overdue invoices'}
        loading={loading}
      />
    </div>
  );
}
